import type { ZoneBoundary } from "@sitescene/types";

import {
	featureToGeoJsonLineString,
	featureToGeoJsonPolygon,
	geoJsonPolygonToOpenLineString,
} from "@sitescene/shared";

export type ZoneBoundaryDrawGeometryType = "line" | "polygon";

const geometryVertexCount = (
	boundary: null | undefined | ZoneBoundary,
): number => {
	if (!boundary) return 0;
	if (boundary.type === "LineString") return boundary.coordinates.length;
	return boundary.coordinates[0]?.length ?? 0;
};

const vertexCount = (feature: GeoJSON.Feature): number => {
	if (feature.geometry.type === "LineString") {
		return feature.geometry.coordinates.length;
	}

	if (feature.geometry.type === "Polygon") {
		return feature.geometry.coordinates[0]?.length ?? 0;
	}

	return 0;
};

export const pickRichestDrawFeature = (
	features: GeoJSON.Feature[],
): GeoJSON.Feature | undefined =>
	[...features].sort(
		(left, right) => vertexCount(right) - vertexCount(left),
	)[0];

const toLineBoundary = (feature: GeoJSON.Feature): null | ZoneBoundary => {
	if (feature.geometry.type === "LineString") {
		return featureToGeoJsonLineString(
			feature as GeoJSON.Feature<GeoJSON.LineString>,
		);
	}

	if (feature.geometry.type === "Polygon") {
		const polygon = featureToGeoJsonPolygon(
			feature as GeoJSON.Feature<GeoJSON.Polygon>,
		);
		return polygon ? geoJsonPolygonToOpenLineString(polygon) : null;
	}

	return null;
};

/**
 * Reads the operator's in-progress Draw feature. Prefers the shape with the
 * most vertices so a leftover marker-connect LineString cannot hide a custom
 * path (or a closed polygon Draw still has on the map).
 */
export const boundaryFromDrawFeatures = (
	features: GeoJSON.Feature[],
	geometryType: ZoneBoundaryDrawGeometryType,
): null | ZoneBoundary => {
	const richest = pickRichestDrawFeature(features);
	if (!richest) return null;

	if (geometryType === "line") {
		return toLineBoundary(richest);
	}

	if (richest.geometry.type === "Polygon") {
		return featureToGeoJsonPolygon(richest as GeoJSON.Feature<GeoJSON.Polygon>);
	}

	return null;
};

/**
 * While the operator is drawing a custom outline, Draw can have more vertices
 * than the last saved/config polygon. Keep that live shape.
 *
 * Do not keep it when switching to Auto (translate-only hull) or when the
 * geometry type changes — otherwise the old outline stays on the map next to
 * the new one.
 */
export const shouldKeepRicherLiveDrawBoundary = ({
	configBoundary,
	geometryType,
	liveBoundary,
	translateOnly = false,
}: {
	configBoundary: null | undefined | ZoneBoundary;
	geometryType: ZoneBoundaryDrawGeometryType;
	liveBoundary: null | ZoneBoundary;
	translateOnly?: boolean;
}): boolean => {
	if (translateOnly) return false;
	if (!configBoundary || !liveBoundary) return false;
	if (liveBoundary.type !== configBoundary.type) return false;
	if (geometryType === "line" && liveBoundary.type !== "LineString") {
		return false;
	}
	if (geometryType === "polygon" && liveBoundary.type !== "Polygon") {
		return false;
	}

	if (
		liveBoundary.type === "Polygon" &&
		configBoundary.type === "Polygon" &&
		!configVerticesLieOnLiveRing(configBoundary, liveBoundary)
	) {
		return false;
	}

	return (
		geometryVertexCount(liveBoundary) > geometryVertexCount(configBoundary)
	);
};

const ringPositions = (boundary: ZoneBoundary): [number, number][] => {
	if (boundary.type === "LineString") {
		return boundary.coordinates.map(
			(position) => [position[0], position[1]] as [number, number],
		);
	}

	return (boundary.coordinates[0] ?? []).map(
		(position) => [position[0], position[1]] as [number, number],
	);
};

const distanceToSegmentSquared = (
	point: [number, number],
	start: [number, number],
	end: [number, number],
): number => {
	const deltaLng = end[0] - start[0];
	const deltaLat = end[1] - start[1];
	if (deltaLng === 0 && deltaLat === 0) {
		const offsetLng = point[0] - start[0];
		const offsetLat = point[1] - start[1];
		return offsetLng * offsetLng + offsetLat * offsetLat;
	}

	const lengthSquared = deltaLng * deltaLng + deltaLat * deltaLat;
	const projection =
		((point[0] - start[0]) * deltaLng + (point[1] - start[1]) * deltaLat) /
		lengthSquared;
	const clamped = Math.max(0, Math.min(1, projection));
	const closestLng = start[0] + clamped * deltaLng;
	const closestLat = start[1] + clamped * deltaLat;
	const offsetLng = point[0] - closestLng;
	const offsetLat = point[1] - closestLat;
	return offsetLng * offsetLng + offsetLat * offsetLat;
};

/** ~1.1 meters. Saved vertices of the same outline should be this close to live Draw. */
const VERTEX_ON_RING_EPSILON_DEGREES_SQUARED = 1e-10;

const configVerticesLieOnLiveRing = (
	configBoundary: ZoneBoundary,
	liveBoundary: ZoneBoundary,
): boolean => {
	const configRing = ringPositions(configBoundary);
	const liveRing = ringPositions(liveBoundary);
	if (configRing.length === 0 || liveRing.length < 2) return false;

	const liveOpen =
		liveRing.length > 1 &&
		liveRing[0]![0] === liveRing[liveRing.length - 1]![0] &&
		liveRing[0]![1] === liveRing[liveRing.length - 1]![1]
			? liveRing.slice(0, -1)
			: liveRing;

	return configRing.every((vertex) => {
		for (let index = 0; index < liveOpen.length; index += 1) {
			const start = liveOpen[index]!;
			const end = liveOpen[(index + 1) % liveOpen.length]!;
			if (
				distanceToSegmentSquared(vertex, start, end) <=
				VERTEX_ON_RING_EPSILON_DEGREES_SQUARED
			) {
				return true;
			}
		}
		return false;
	});
};
